// 2x vehicle artwork and legacy weapon helpers. Detailed people live in character-art/character-rig.
// Textures are HD pixels, shown at scale 0.5 so one texel is half a world pixel.
import { Texture } from 'pixi.js';
import { VEHICLES, type VehicleType } from '../../common/src/data';
import { hash2 } from '../../common/src/util';
import { PAINTS, canvas, tex } from './art';
import { K, px, disc, dk, lt, mix, alpha, frame } from './hd-kit';
/** 1 px dark outline around everything drawn (readability rule from the first playtest) */
export function outline(src: HTMLCanvasElement) {
  const [c, g] = canvas(src.width, src.height);
  const [m, mg] = canvas(src.width, src.height); mg.drawImage(src, 0, 0); mg.globalCompositeOperation = 'source-in'; mg.fillStyle = K.ink; mg.fillRect(0, 0, src.width, src.height);
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) g.drawImage(m, dx, dy);
  g.drawImage(src, 0, 0); return c;
}

// People use the detailed articulated artwork; four-view facingOf stays compatible with the boss renderer.
export { CHAR_W, CHAR_H, personCanvas } from './character-art';
export type Facing = 'S' | 'N' | 'E' | 'W';
export function facingOf(a: number): Facing {
  const c = Math.cos(a), s = Math.sin(a);
  return Math.abs(c) > Math.abs(s) * 1.1 ? (c > 0 ? 'E' : 'W') : s > 0 ? 'S' : 'N';
}
const charCache = new Map<string, Texture>(); // legacy weapon helpers and foot shadows

export function gunTex(long: boolean): Texture {
  const key = `gun${long}`; const hit = charCache.get(key); if (hit) return hit;
  const L = long ? 18 : 11;
  const [c, g] = canvas(L + 2, 6);
  px(g, 1, 2, L, 2, K.metalLo); px(g, 1, 2, L, 1, K.metalHi); px(g, 2, 3, 3, 2, K.ink); if (long) px(g, 8, 1, 4, 1, K.metal);
  const t = tex(outline(c)); charCache.set(key, t); return t;
}

/** A katana seen from above, pointing +x; pivot at the grip (anchor x = 0.15 like the gun): wrapped hilt, amber guard, pale blade. */
export function katanaTex(): Texture {
  const hit = charCache.get('katana'); if (hit) return hit;
  const [c, g] = canvas(26, 7);
  px(g, 1, 3, 5, 2, K.pink); px(g, 2, 3, 1, 2, K.ink); px(g, 4, 3, 1, 2, K.ink); // hilt with wrap
  px(g, 6, 1, 2, 6, K.amber); // guard
  px(g, 8, 2, 16, 3, K.white); px(g, 8, 4, 16, 1, K.metalHi); px(g, 23, 3, 1, 1, K.white); // blade and its edge
  const t = tex(outline(c)); charCache.set('katana', t); return t;
}

/** A neon tanto from above, pointing +x, pivot at the grip like the katana: a short blade whose glow and core are the wielder's crew mask color. */
export function tantoTex(color: string): Texture {
  const key = 'tanto' + color; const hit = charCache.get(key); if (hit) return hit;
  const [c, g] = canvas(16, 7);
  px(g, 1, 3, 4, 2, K.ink); px(g, 2, 3, 1, 2, color); // grip with a glowing wrap
  px(g, 5, 2, 1, 4, K.amber); // small guard
  g.globalAlpha = 0.45; px(g, 6, 1, 9, 5, color); g.globalAlpha = 1; // glow halo
  px(g, 6, 2, 8, 3, color); px(g, 6, 3, 9, 1, K.white); // blade body and hot core
  const t = tex(outline(c)); charCache.set(key, t); return t;
}

/** soft ellipse under feet / wheels */
export function shadowTex(w: number, h: number): Texture {
  const key = `sh${w}x${h}`; const hit = charCache.get(key); if (hit) return hit;
  const [c, g] = canvas(w, h);
  disc(g, Math.floor(w / 2), Math.floor(h / 2), Math.floor(w / 2) - 1, Math.floor(h / 2) - 1, alpha(K.night, 0.35));
  disc(g, Math.floor(w / 2), Math.floor(h / 2), Math.floor(w / 2) - 3, Math.floor(h / 2) - 2, alpha(K.night, 0.35));
  const t = tex(c); charCache.set(key, t); return t;
}

// ------------------------------------------------------------------ vehicles (front is +x)
const vehCache = new Map<string, Texture>();
/** Same world footprint, with four transparent pixels around mirrors and tyres. */
export function carCanvas(t: VehicleType, paint: number, wrecked: boolean, gunner: boolean, deco = 0): HTMLCanvasElement {
  const s = VEHICLES[t], L = s.len * 2, W = s.wid * 2, o = 4, cy = o + W / 2;
  const [c, g] = canvas(L + 8, W + 8);
  let body = PAINTS[((paint % PAINTS.length) + PAINTS.length) % PAINTS.length];
  if (t === 'police') body = K.white;
  if (t === 'armored') body = dk(K.slate, .05);
  if (t === 'autocab') body = lt(K.cyan, .72);
  if (wrecked) body = dk(K.slate, .52);
  const hi = lt(body,.34), lo = dk(body,.4), edge = dk(body,.62);
  const R = (x:number,y:number,w:number,h:number,col:string) => px(g,Math.round(x),Math.round(y),Math.round(w),Math.round(h),col);
  const P = (points:number[][], col:string) => {
    for(let y=Math.ceil(Math.min(...points.map(p=>p[1])));y<Math.ceil(Math.max(...points.map(p=>p[1])));y++){
      const cuts:number[]=[];
      for(let i=0;i<points.length;i++){const a=points[i],b=points[(i+1)%points.length];if((a[1]<=y+.5&&b[1]>y+.5)||(b[1]<=y+.5&&a[1]>y+.5))cuts.push(a[0]+(y+.5-a[1])/(b[1]-a[1])*(b[0]-a[0]));}
      cuts.sort((a,b)=>a-b);for(let i=0;i+1<cuts.length;i+=2)R(Math.round(cuts[i]),y,Math.round(cuts[i+1])-Math.round(cuts[i]),1,col);
    }
  };
  const vent=(x:number,y:number,w:number,n:number)=>{for(let i=0;i<n;i++){R(x,y+i*2,w,1,K.ink);R(x,y+i*2+1,w,1,K.metalLo);}};
  const glass=(x:number,y:number,w:number,h:number)=>{
    R(x,y,w,h,K.ink);R(x+1,y+1,w-2,h-2,K.glassLo);
    R(x+2,y+2,Math.max(1,w-4),Math.max(1,h-4),K.glass);
    R(x+1,y+1,1,h-3,K.glassHi);R(x+2,y+2,Math.max(1,w-3),1,lt(K.glassHi,.25));
    for(let i=0;i<Math.min(w-3,h-4);i++)R(x+2+i,y+h-3-i,1,1,dk(K.cyan,.6));
  };
  if(t==='bike'){
    // Separate fork, wheels, shaped tank, leather saddle and rear engine/exhaust.
    for(const x of [o,o+L-6]){R(x,cy-2,6,5,K.ink);R(x+1,cy-1,4,1,K.metalLo);R(x+2,cy+2,3,1,K.metalHi);}
    R(o+5,cy-1,L-10,3,K.metal);R(o+7,cy+4,14,2,K.metalLo);R(o+18,cy+4,5,1,K.metalHi);
    P([[o+7,cy-4],[o+18,cy-5],[o+24,cy-2],[o+24,cy+3],[o+16,cy+5],[o+7,cy+3]],body);
    R(o+14,cy-3,6,2,hi);R(o+20,cy-1,3,3,lo);R(o+5,cy-2,9,5,K.ink);R(o+6,cy-2,7,1,K.metalLo);
    R(o+9,cy-1,1,3,K.metal);R(o+12,cy-1,1,3,K.metalLo);R(o+17,cy-1,2,2,K.metalHi);
    R(o+23,cy-5,2,11,K.metalHi);R(o+22,cy-5,4,2,K.ink);R(o+22,cy+4,4,2,K.ink);
    R(o+24,cy-2,2,4,K.glassHi);R(o+L-2,cy-1,2,3,wrecked?K.metalLo:K.warmHi);R(o+4,cy-1,1,3,K.red);
    // Tread on both tyres, bar-end mirrors, a hot exhaust tip, tail lamp core, saddle stitching and a tank stripe;
    // the Kaminari-gumi's pink bikes (paint 6) carry an amber lightning bolt.
    for(const x of [o,o+L-6])for(let i=1;i<6;i+=2){R(x+i,cy-2,1,1,K.tarHi);R(x+i,cy+2,1,1,K.tarHi);}
    R(o+23,cy-7,2,1,K.ink);R(o+23,cy+6,2,1,K.ink);R(o+24,cy-7,1,1,K.metalHi);R(o+24,cy+6,1,1,K.metalHi);
    R(o+6,cy+4,1,2,K.ink);if(!wrecked)R(o+7,cy+5,1,1,K.warmLo);R(o+4,cy,1,1,wrecked?K.metalLo:K.pink);
    R(o+7,cy-1,1,1,K.metal);R(o+11,cy+1,1,1,K.metal);
    if(!wrecked){if(paint===6){
      // Big amber bolt across the tank, white-hot core.
      R(o+19,cy-4,2,1,K.amber);R(o+18,cy-3,2,1,K.amber);R(o+17,cy-2,4,1,K.amber);R(o+18,cy-1,2,1,K.amber);R(o+17,cy,2,1,K.amber);R(o+16,cy+1,2,1,K.amber);
      R(o+18,cy-2,1,1,K.white);R(o+16,cy+2,1,1,K.warmHi);}
    else R(o+15,cy+1,6,2,lt(body,.7));}
  }else{
    const sports=t==='sports', apc=t==='armored';
    const axles=apc?[o+5,o+23,o+L-12]:[o+5,o+L-11];
    for(const x of axles)for(const y of [o-1,o+W-2]){
      // Tyre with tread blocks on the visible edge, a rim ring and a bright hub nut.
      R(x,y,7,4,K.ink);const out=y<o?y:y+3;for(let i=1;i<7;i+=2)R(x+i,out,1,1,K.tarHi);
      R(x+1,y+1,5,2,K.metalLo);R(x+2,y+1,3,2,K.metal);R(x+2,y+2,3,1,K.metalHi);R(x+3,y+1+(y<o?1:0),1,1,K.white);
    }
    P([[o+3,o],[o+L-5,o],[o+L,o+4],[o+L,o+W-4],[o+L-5,o+W],[o+3,o+W],[o,o+W-3],[o,o+3]],body);
    R(o+4,o+1,L-10,1,hi);R(o+2,o+3,1,W-6,hi);R(o+3,o+W-3,L-7,2,lo);R(o+5,o+W-1,L-12,1,edge);
    // End caps, lower rockers, vents and separate front/rear lamp housings.
    R(o,o+4,2,W-8,K.metalLo);R(o+L-2,o+5,2,W-10,edge);
    R(o+L-1,cy-3,1,6,K.ink);R(o+L-2,cy-2,1,4,K.metalHi);
    R(o+4,o+W-3,L-9,1,hi);
    for(const y of [o+3,o+W-6]){
      // Headlamp: dark housing, warm lens, white glint; tail lamp: red lens with a hot pink core.
      R(o+L-4,y,4,3,K.ink);R(o+L-3,y,3,2,wrecked?K.metalLo:K.warm);R(o+L-2,y,2,2,wrecked?K.metalLo:K.warmHi);
      if(!wrecked)R(o+L-1,y,1,1,K.white);
      R(o,y,2,3,K.ink);R(o,y,1,3,wrecked?K.metalLo:K.redLo);R(o,y+1,1,1,wrecked?K.metalLo:K.red);R(o+1,y+1,1,1,wrecked?K.ink:K.pink);
    }
    // Grille slats between the lamps and twin exhaust tips at the rear.
    for(let y=cy-2;y<cy+2;y+=2)R(o+L-2,y,1,1,K.metalLo);
    R(o-1,o+W-8,1,1,K.metalHi);R(o-1,o+W-6,1,1,K.metalHi);
    R(o+1,cy-2,1,4,K.white);R(o+1,cy-1,1,2,K.metalLo);
    if(t==='kei'){
      // Open ribbed cargo bed, tied crates, cab pillars and bumper guard.
      R(o+3,o+3,17,W-6,edge);R(o+4,o+4,15,W-8,K.metalLo);
      for(let x=o+5;x<o+18;x+=3)R(x,o+4,1,W-8,K.metalHi);
      frame(g,o+3,o+3,17,W-6,hi);R(o+9,o+6,7,7,K.wood);frame(g,o+9,o+6,7,7,K.woodHi);
      R(o+12,o+6,1,7,K.ink);R(o+9,o+9,7,1,K.ink);
      if(gunner&&!wrecked){R(o+6,cy-3,7,6,dk(K.cyan,.5));disc(g,o+9,cy,2,2,K.ink);R(o+11,cy-1,10,2,K.metalHi);}
      R(o+21,o+2,12,W-4,hi);glass(o+28,o+3,5,W-6);glass(o+20,o+4,3,W-8);
      R(o+24,o+4,1,W-8,body);R(o+21,o-1,3,2,K.metalLo);R(o+21,o+W-1,3,2,K.metalLo);
      R(o+L-1,o+5,1,W-10,K.metalHi);
      // Rope over the crate, tailgate chain, cab roof beacon and ladder rack, mud flaps behind the rear wheels.
      if(!gunner||wrecked){R(o+9,o+6,1,1,K.warmLo);R(o+11,o+8,1,1,K.warmLo);R(o+13,o+10,1,1,K.warmLo);R(o+15,o+12,1,1,K.warmLo);}
      for(let y=o+4;y<o+W-4;y+=2)R(o+2,y,1,1,K.metalHi);
      R(o+25,o+3,1,W-6,K.metalLo);R(o+27,o+3,1,W-6,K.metalLo);
      R(o+25,cy-1,3,2,K.ink);R(o+26,cy-1,1,1,wrecked?K.metalLo:K.amber);
      R(o+12,o-1,1,2,K.ink);R(o+12,o+W-1,1,2,K.ink);
    }else if(apc){
      // Heavy six-wheel APC: slat armour, radiator banks, hatches, bolts and view ports.
      for(const y of [o+2,o+W-5])for(let x=o+5;x<o+L-6;x+=8){R(x,y,7,3,lo);R(x,y,6,1,hi);R(x+1,y+1,1,1,K.metalHi);}
      R(o+12,o+6,29,W-12,edge);R(o+13,o+7,27,W-14,body);frame(g,o+17,o+8,12,10,hi);
      R(o+20,o+10,6,1,K.metalHi);R(o+22,o+12,2,4,lo);vent(o+5,o+7,5,7);
      glass(o+43,o+6,5,6);glass(o+43,o+W-12,5,6);
      R(o+32,cy-5,6,10,lo);R(o+33,cy-4,4,1,hi);R(o+35,cy-2,1,4,K.amber);
      for(const x of [o+13,o+39])for(const y of [o+7,o+W-8])R(x,y,1,1,K.white);
      R(o+L-3,o+8,2,W-16,K.ink);R(o+1,o+5,1,W-10,K.amber);
      // Hazard chevrons on the nose plate, smoke dischargers, whip antenna and a Kanemori diamond on the hatch.
      // Chevrons sit inward on the bumper plate: diagonal amber/ink bands pointing forward.
      const pl=o+L-8,pt=o+7,ph=W-14;R(pl,pt,5,ph,K.ink);
      for(let y=0;y<ph;y++)for(let x=0;x<5;x++)if(((x+Math.abs(y-(ph-1)/2)|0)>>1)%2===0)R(pl+x,pt+y,1,1,wrecked?K.metalLo:K.amber);
      for(const y of [o+4,o+W-5])for(let i=0;i<3;i++)R(o+40+i*2,y,1,1,K.ink);
      for(let i=0;i<4;i++)R(o+9+i,o+5+i,1,1,K.metalHi);
      R(o+23,o+11,1,3,K.amber);R(o+22,o+12,3,1,K.amber);R(o+23,o+12,1,1,K.warmHi);
    }else{
      const rx=o+Math.floor(L*(sports?.28:.3)), rw=Math.floor(L*(sports?.3:.34));
      // Dark window band surrounds the raised roof, with visible pillars and seats.
      P([[rx-6,o+4],[rx+rw+5,o+3],[rx+rw+8,o+6],[rx+rw+7,o+W-5],[rx-5,o+W-4]],K.ink);
      glass(rx-5,o+4,5,W-8);glass(rx+rw,o+4,7,W-8);
      R(rx,o+3,rw,W-6,lo);R(rx+1,o+4,rw-2,W-9,lt(body,.15));R(rx+1,o+4,rw-3,1,hi);
      for(const y of [o+2,o+W-5]){
        R(rx+1,y,rw-3,3,K.glass);R(rx+2,y,4,1,K.glassHi);R(rx+Math.floor(rw*.48),y,2,3,K.ink);
        R(rx+3,y+1,3,1,K.metalLo);R(rx+rw-5,y+1,3,1,K.metalLo);
      }
      R(rx+rw-1,o-1,3,3,lo);R(rx+rw-1,o+W-2,3,3,lo);R(rx+rw,o-1,2,1,hi);
      for(const y of [o+1,o+W-2]){R(rx+1,y,3,1,K.metalHi);R(rx+rw-5,y,3,1,K.metalHi);}
      R(rx+rw+7,o+5,1,W-10,edge);R(o+4,o+4,1,W-8,lo);
      // Panel lines (door, fender and rear-quarter seams), hood creases, trunk lid seam and paint glints.
      // Strong contrast so the detail survives 1:1: near-ink seams, bright creases and white-hot glints.
      const hx=rx+rw+9, hw=o+L-5-hx, shut=mix(body,K.ink,.8);
      for(const y of [o,o+W-3]){R(rx+Math.floor(rw*.48),y,1,3,shut);R(rx+rw+6,y,1,3,shut);R(rx-2,y,1,3,shut);}
      R(hx,o+6,hw,1,lt(body,.45));R(hx,o+W-7,hw,1,dk(body,.55));R(hx+2,o+5,4,1,lt(body,.8));R(hx+1,o+5,1,1,K.white);R(hx+2,o+6,1,1,K.white);
      R(rx-7,o+5,1,W-10,shut);R(o+5,o+5,3,1,lt(body,.75));R(o+5,o+5,1,1,K.white);
      // Sports decal set: picked from the vehicle id and paint, so a respray can change it. Beaters stay plain (owner, 2026-10-15).
      const set=deco%3, decal=!wrecked&&sports;
      if(decal&&set===2){R(rx+1,o+4,rw-2,W-8,dk(body,.7));R(rx+1,o+4,rw-3,1,dk(body,.45));} // two-tone roof
      if(sports){
        R(o+3,o+1,3,W-2,K.ink);R(o+3,o+2,1,W-4,K.metalHi); // wing
        R(rx+rw+9,o+6,L-(rx-o)-rw-13,2,K.ink);R(rx+rw+9,o+W-8,L-(rx-o)-rw-13,2,K.ink);
        R(rx+2,cy-2,rw-4,1,K.amber);R(rx+2,cy+1,rw-4,1,K.amber);
        R(o+L-3,cy-3,2,6,K.ink);R(o+2,o+6,1,2,K.metalHi);R(o+2,o+W-8,1,2,K.metalHi);
        // Set 0: bold twin racing stripes over hood and deck; set 1: a door roundel; side intakes behind the doors.
        if(decal&&set===0){for(const y of [cy-3,cy+1]){R(hx,y,hw+3,2,K.white);R(o+5,y,rx-o-12,2,K.white);R(hx,y+1,hw+3,1,lt(body,.6));}}
        if(decal&&set===1){disc(g,hx+5,cy,3,3,K.ink);disc(g,hx+5,cy,2,2,K.white);R(hx+5,cy-1,1,3,K.ink);}
        for(const y of [o+1,o+W-2])R(rx-5,y,3,1,K.ink);
      }else if(t==='police'){
        // Keep the windshield visible: black hood/deck and a separate roof light bar.
        R(o+4,o+5,rx-o-10,W-10,K.ink);R(rx+rw+8,o+5,L-(rx-o)-rw-11,W-10,K.ink);
        R(rx+5,o+3,4,W-6,K.ink);R(rx+6,o+4,2,(W-8)/2,wrecked?K.metalLo:K.cyan);R(rx+6,cy,2,(W-8)/2,wrecked?K.metalLo:K.pink);
        R(rx+10,cy-2,3,4,K.ink);R(rx+11,cy-1,1,2,K.white);R(o+L-1,o+6,1,W-12,K.metalHi);
        // KSD livery: cyan door stripe, gold hood crest, push bar, A-pillar spotlight, trunk antenna.
        // Split door stripe echoes the light bar (cyan front, pink rear); a bigger gold crest on the hood.
        if(!wrecked){const sw=rw+8,half=Math.floor(sw/2);for(const y of [o,o+W-2]){R(rx-3,y,half,2,K.pink);R(rx-3+half,y,sw-half,2,K.cyan);}
          R(hx+2,cy-2,4,4,K.amber);R(hx+3,cy-1,2,2,K.warmHi);R(hx+3,cy-1,1,1,K.white);R(o+6,cy-1,1,2,K.amber);}
        R(o+L,o+6,1,W-12,K.ink);R(o+L,o+7,1,W-14,K.metalLo);R(rx+rw+1,o+3,1,1,K.white);R(o+6,o+W-7,1,1,K.metalHi);
      }else if(t==='autocab'){
        R(rx+2,o+5,rw-4,W-10,K.glass);R(rx+3,o+6,rw-6,1,K.glassHi);
        disc(g,rx+Math.floor(rw/2),cy,3,3,K.ink);disc(g,rx+Math.floor(rw/2),cy,2,2,wrecked?K.metalLo:K.cyan);
        R(rx+Math.floor(rw/2),cy-1,1,1,K.white);R(o+4,o+2,L-10,1,K.cyan);R(o+4,o+W-3,L-10,1,K.cyan);
        R(rx+rw+9,cy-1,3,2,K.cyan);
        // Kanemori checker band along both flanks, and a pink fare lamp behind the sensor dome.
        if(!wrecked)for(const y of [o,o+W-2])for(let x=o+6;x<o+L-6;x+=2)if(x<rx-3||x>rx+rw+7){R(x,y,2,1,(x>>1)&1?K.ink:K.white);R(x,y+1,2,1,(x>>1)&1?K.white:K.ink);}
        R(rx+2,cy-1,2,2,K.ink);R(rx+2,cy-1,1,1,wrecked?K.metalLo:K.pink);
      }else{
        // A worn commuter: replacement hood, recessed handles and a restrained scuff.
        R(rx+rw+9,o+6,4,W-12,dk(body,.16));R(o+6,o+5,3,1,lo);R(o+7,o+6,1,2,K.metalLo);
        R(rx+3,o+W-2,5,1,edge);R(rx+5,o+W-2,2,1,K.metalHi);
        // Mismatched primer door, rust on the rear quarter, a dent in the hood and a cyan bumper sticker.
        R(rx+Math.floor(rw*.48)+1,o+W-3,Math.floor(rw*.5)-2,2,mix(body,K.metal,.65));
        R(o+6,o+W-4,2,1,K.woodLo);R(o+7,o+W-3,3,1,K.wood);R(o+9,o+W-4,1,1,K.woodLo);
        R(hx+8,o+W-8,2,1,lo);R(hx+8,o+W-9,2,1,hi);if(!wrecked)R(o+2,o+W-9,1,2,K.cyan);
      }
    }
  }
  if(wrecked){
    g.save();g.globalCompositeOperation='source-atop';
    for(let i=0;i<22;i++){const x=o+Math.floor(hash2(i,L)*L),y=o+Math.floor(hash2(W,i)*W);R(x,y,3,2,K.ink);if(i%3===0)R(x,y,2,1,K.woodLo);}
    R(o+L*.65,o+W*.25,2,W*.5,K.ink);R(o+L*.65-3,cy,7,1,K.metalLo);g.restore();
  }
  const out=outline(c);
  if(t==='bike'&&paint===6&&!wrecked){
    // Faint pink underglow behind the Kaminari bike, inside the transparent margin (silhouette unchanged).
    const og=out.getContext('2d')!;og.save();og.globalCompositeOperation='destination-over';
    og.fillStyle=alpha(K.pink,.22);og.fillRect(o+3,cy-5,L-6,11);og.fillStyle=alpha(K.pink,.14);og.fillRect(o+1,cy-6,L-2,13);og.restore();
  }
  return out;
}
/** Civilian decal set (0-2) for a vehicle: deterministic from its id and paint, so a respray can change it too. */
export function decoOf(id: number, paint: number) { return Math.floor(hash2(id, paint + 17) * 3) % 3; }
export function carTex(t: VehicleType, paint: number, wrecked: boolean, gunner: boolean, deco = 0): Texture {
  const pc=((paint%PAINTS.length)+PAINTS.length)%PAINTS.length;
  const d=t==='sports'?deco%3:0;
  const key=`${t}:${pc}:${wrecked}:${gunner && t==='kei'}:${d}`;
  const hit=vehCache.get(key);if(hit)return hit;
  const texture=tex(carCanvas(t,pc,wrecked,gunner,d));vehCache.set(key,texture);return texture;
}
